/**
 * Zone detection + braking/throttle classification knobs.
 *
 * Tuning any value here: edit → `npm run build` in src/PitBrain.Shared →
 * restart/rebuild the clients.
 */
import type {
  ZoneDetectionConfig,
  NoiseToleranceConfig,
} from '../zones/TrackZoneTypes'

export const DEFAULT_ZONE_DETECTION_CONFIG: ZoneDetectionConfig = {
  minZoneLength: 20,
  brakingThreshold: -2,
  corneringThreshold: 10,
  accelerationThreshold: 2,
  // Misnomer: counts LAPS with usable data, not samples — analyzeTrackZones
  // returns the empty result below this many laps. Comparison map overlays
  // override it to 2 (COMPARISON_ZONE_DETECTION_CONFIG in mobile).
  minSamplesPerZone: 4,
  boundaryTolerance: 10,
  minCornerSpeedDropPercent: 10,
  cornerBrakingAdjacencyMeters: 30,
  cornerVMinRisePct: 3,
}

export const DEFAULT_NOISE_TOLERANCE: NoiseToleranceConfig = {
  gpsAccuracy: 3,
  speedNoise: 2,
  accelerationNoise: 0.05,
  minDistanceVariance: 8,
  minSpeedVariance: 8,
  minGVariance: 0.15,
}

/** Unit: metres. Default: 5. Bucket size for distance-aligning laps before
 * consensus voting. */
export const DISTANCE_BUCKET_SIZE = 5
/** Unitless fraction. Default: 0.02. Lap lengths differing by more than
 * this trigger distance alignment (typical GPS variance is 1–3%). */
export const LAP_LENGTH_MISMATCH_FRACTION = 0.02
/** Unitless ratio. Default: 1.5. Sampling-interval mismatch beyond this
 * triggers distance alignment. */
export const SAMPLING_INTERVAL_MISMATCH_RATIO = 1.5

// ⚠ Two independently-tuned braking thresholds exist (pre-pass-24 state,
// surfaced deliberately — unifying them changes rider-visible numbers and
// needs its own owner decision):
/** Unit: g. Default: -0.15. Braking classification for the consensus /
 * legacy-variance path (LapZoneMetrics → ZoneConsensus/ZoneVariance). */
export const CONSENSUS_BRAKING_THRESHOLD_G = -0.15
/** Unit: g. Default: -0.1. "Active braking" gate for the rider-visible
 * per-lap table path (ZoneLapData → brake-point rows/markers). */
export const LAP_TABLE_BRAKING_G = -0.1
/** Unit: g. Default: -0.3. Braking is "released" above this (trail/engine
 * braking) — shared by both paths. 0.3g is the ceiling of what engine
 * braking + aero drag produce without the lever, so decel beyond it is
 * defensibly lever braking. */
export const BRAKE_RELEASE_G = -0.3
/** Unitless. Default: 1.5. Reliability gate for the brake-release MARKER:
 * a lap's release point is only rendered when its peak braking reaches this
 * multiple of |BRAKE_RELEASE_G| (0.45g). Below that the release crossing
 * sits in the noise band just past the lap's own peak, so the marker
 * position is arbitrary. Markers only — consistency rows and variance still
 * see every release point (owner decision 2026-08-24). */
export const BRAKE_RELEASE_RELIABILITY_MULTIPLE = 1.5
/** Unit: g. Default: 0.1. Throttle-application gate — shared by both paths. */
export const THROTTLE_G = 0.1
